import { useEffect, useRef, useState } from 'react';
import { useBoolean, useCountdown, useUnmount } from 'usehooks-ts';
import { useConfigurationStore } from '@/stores/configuration';
import { addTranslateToast } from '@/stores/toast';
import { createPromiseWithResolvers, type PromiseWithResolvers } from '@/utils/promise';

export interface RecorderControls {
  startRecording: () => void;
  stopRecording: () => Promise<Blob>;
  resetRecording: () => void;
  cancelRecording: () => void;
  isRecording: boolean;
  canRecord: boolean;
  duration: number;
  getMediaRecorder: () => MediaRecorder | undefined;
}

export type MediaAudioTrackConstraints = Pick<
  MediaTrackConstraints,
  | 'deviceId'
  | 'groupId'
  | 'autoGainControl'
  | 'channelCount'
  | 'echoCancellation'
  | 'noiseSuppression'
  | 'sampleRate'
  | 'sampleSize'
>;

export default function useAudioRecorder(
  onRecorded: (blob: Blob, duration: number) => void,
  {
    audioTrackConstraints,
    onNotAllowedOrFound,
    mediaRecorderOptions,
  }: {
    audioTrackConstraints?: MediaAudioTrackConstraints;
    onNotAllowedOrFound?: (exception: DOMException) => unknown;
    mediaRecorderOptions?: MediaRecorderOptions;
  } = {},
): RecorderControls {
  const maxDuration = useConfigurationStore((s) => s.envConfig.config?.audioMaxDuration ?? 60);
  const isLoading = useRef(false);
  const [isRecording, setRecording] = useState(false);
  const { value: canRecord, setValue: setCanRecord } = useBoolean(true);
  const [duration, { startCountdown, stopCountdown, resetCountdown }] = useCountdown({
    countStart: 0,
    countStop: Number.POSITIVE_INFINITY,
    intervalMs: 10,
    isIncrement: true,
  });
  const recordingBlob = useRef<PromiseWithResolvers<Blob>>();
  const mediaRecorder = useRef<MediaRecorder>();

  const initializeRecorder = async () => {
    const stream = await navigator.mediaDevices
      .getUserMedia({ audio: audioTrackConstraints ?? true })
      .catch((err: DOMException) => {
        setCanRecord(false);
        console.error(err);
        onNotAllowedOrFound?.(err);
        throw err;
      });

    setCanRecord(true);
    setRecording(true);

    if (!stream) throw new Error('STREAM_NOT_AVAILABLE');

    const recorder = new MediaRecorder(stream, mediaRecorderOptions);

    recorder.addEventListener('dataavailable', (event) => {
      recordingBlob.current?.resolve(event.data);
      mediaRecorder.current = undefined;
    });

    recorder.addEventListener('stop', async () => {
      const audioContext = new AudioContext();
      audioContext.createMediaStreamSource(recorder.stream).disconnect();
      await audioContext.close();
      for (const t of recorder.stream.getTracks()) {
        t.stop();
      }
      mediaRecorder.current = undefined;
    });

    mediaRecorder.current = recorder;
    recordingBlob.current = createPromiseWithResolvers<Blob>();

    return recorder;
  };

  const startRecording = async () => {
    if (isRecording) return;

    const recorder = await initializeRecorder();
    recorder.start();
    resetCountdown();
    startCountdown();
  };

  const stopRecording = async (cancel = false) => {
    mediaRecorder.current?.stop();
    stopCountdown();
    setRecording(false);

    if (duration < 100) {
      addTranslateToast({
        type: 'warning',
        message: 'conversation.recording.min-duration|second=1',
      });
    } else if (!cancel) {
      const blob = (await recordingBlob.current?.promise) ?? new Blob();
      onRecorded(blob, Math.round(duration / 100));
      recordingBlob.current = createPromiseWithResolvers<Blob>();
      resetCountdown();
      return blob;
    }

    return Promise.reject('CANCELED');
  };

  const resetRecording = () => {
    stopCountdown();
    resetCountdown();
    setRecording(false);
    mediaRecorder.current?.stop();
    recordingBlob.current = createPromiseWithResolvers<Blob>();
  };

  const cancelRecording = () => {
    stopCountdown();
    resetCountdown();
    setRecording(false);
    stopRecording(true).catch(console.warn);
  };

  useEffect(() => {
    if (isRecording && maxDuration && duration / 100 >= maxDuration) {
      stopRecording().catch(console.warn);
    }
  }, [duration, maxDuration, isRecording]);

  useUnmount(() => {
    mediaRecorder.current?.stop();
    mediaRecorder.current = undefined;
    recordingBlob.current = undefined;
    isLoading.current = false;
  });

  return {
    startRecording,
    stopRecording: () => stopRecording(),
    resetRecording,
    cancelRecording,
    isRecording,
    canRecord,
    duration: Math.round(duration / 100),
    getMediaRecorder: () => mediaRecorder.current,
  };
}
